import { COACH_PRESENCE_CLIENT_ID, COACH_PRESENCE_COACH_ID } from '@cf-shared/data-model/coach';
import type { PresenceState } from './coach-presence-helpers';

/**
 * The part of CoachPresenceService this state machine uses. Declared as a
 * narrow structural type so a unit test can hand in a plain fake object without
 * pulling in Angular, PouchDB or the network.
 */
export interface PresenceRunner {
  startHeartbeat(docId: string, displayName: string): void;
  stopAllHeartbeats(): void;
  watchPresence(docId: string, callback: (state: PresenceState) => void): () => void;
}

/** The signed-in user, as much of it as presence setup needs. */
export interface PresenceUser {
  name?: string;
}

/**
 * Which side of a coach/client pair this device is currently acting as, and so
 * which heartbeat it writes and whose presence it watches. Both readers are
 * functions rather than plain values because the original code only asked
 * whether the user has a coach when it had already established that this device
 * is NOT the coach — and that question loads a document.
 */
export interface PresenceRoleReader {
  /** True when this device is a coach currently managing one of their clients. */
  isManagingClient(): boolean;
  /** True when the signed-in user has a coach of their own. */
  hasCoach(): boolean;
}

/**
 * Coach/client "who is online" presence as the app shell sees it (see
 * design-docs/coach-mode.md, "Presence / Who's Online"). Owns the two flags the
 * shell renders and the two teardown handles that stop the watchers.
 *
 * This is the plain half of `CoachPresenceStateService`, which adds the Angular
 * wiring and the user-database reads. It is split out so the behavior can be
 * unit tested — a test cannot import the user-database service, because loading
 * it pulls in PouchDB, which does not run under the node test runner.
 *
 * Lifted out of `AppComponent` unchanged.
 */
export class CoachPresenceState {
  /** Driven by `coach-presence/client` while in manage mode — shown as a dot on the coach banner. */
  clientOnline = false;
  /** Driven by `coach-presence/coach` while the client app is open — shows the floating "coach viewing" icon. */
  coachIsManaging = false;

  /**
   * Called whenever a change here needs the app shell to re-render. The owner
   * (AppComponent) sets this to its own markForCheck. Left null in tests.
   */
  onChanged: (() => void) | null = null;

  private clientPresenceCleanup: (() => void) | null = null;
  private coachPresenceCleanup: (() => void) | null = null;

  constructor(private presence: PresenceRunner) {}

  /**
   * Start the heartbeat + watcher appropriate for the current mode (coach
   * managing a client OR client with a coach). No-ops in all other cases.
   * Safe to call multiple times — prior watchers/timers are torn down first.
   */
  setup(user: PresenceUser | null | undefined, roles: PresenceRoleReader): void {
    this.teardown();

    const displayName = user?.name || '';

    if (roles.isManagingClient()) {
      // coach side: heartbeat as coach, watch the client's presence doc
      this.presence.startHeartbeat(COACH_PRESENCE_COACH_ID, displayName);
      this.clientPresenceCleanup = this.presence.watchPresence(COACH_PRESENCE_CLIENT_ID, (state) => {
        this.clientOnline = state.online;
        this.onChanged?.();
      });
      return;
    }

    // client side: heartbeat as client (only if they actually have a coach)
    if (!roles.hasCoach()) return;
    this.presence.startHeartbeat(COACH_PRESENCE_CLIENT_ID, displayName);
    this.coachPresenceCleanup = this.presence.watchPresence(COACH_PRESENCE_COACH_ID, (state) => {
      this.coachIsManaging = state.online;
      this.onChanged?.();
    });
  }

  /**
   * Tear down every presence heartbeat + watcher and reset the reactive
   * state flags. Idempotent. Called from `stopManagingClient`, the
   * logout-shutdown path, and `ngOnDestroy` — also re-invoked at the
   * top of `setup` so repeated setup calls don't leak prior timers/watchers.
   */
  teardown(): void {
    this.presence.stopAllHeartbeats();
    this.clientPresenceCleanup?.();
    this.clientPresenceCleanup = null;
    this.coachPresenceCleanup?.();
    this.coachPresenceCleanup = null;
    this.clientOnline = false;
    this.coachIsManaging = false;
  }
}
